import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

// Animated water rendered in WebGL: a perspective plane lit per pixel from a
// sum of sine waves (fresnel sky reflection, sun sheen, haze toward the horizon).
// CSS can't light a surface per pixel, hence WebGL; the canvas keeps a CSS
// gradient as the fallback when WebGL is unavailable.
const MAX_PIXEL_RATIO = 1.5;

const VERTEX_SHADER = `
attribute vec2 p;
void main() { gl_Position = vec4(p, 0.0, 1.0); }
`;

const FRAGMENT_SHADER = `
precision highp float;
uniform vec2 uRes;
uniform float uTime;
const float uRipple = 1.3, uSun = 1.8, uSpeed = 0.40, uHaze = 0.55;

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }

float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i), hash(i + vec2(1, 0)), f.x),
    mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), f.x),
    f.y
  );
}

// Height plus x/z slope of the surface, summed over directional sine waves.
vec3 waves(vec2 p, float t) {
  float h = 0.0, dx = 0.0, dz = 0.0;
  #define W(ax, az, fr, am, sp) { vec2 d = normalize(vec2(ax, az)); float ph = dot(d, p) * fr + t * sp; float c = cos(ph); h += am * sin(ph); dx += am * fr * d.x * c; dz += am * fr * d.y * c; }
  W(0.18, 1.0, 1.2, 0.010, 1.2)
  W(0.20, 1.0, 2.3, 0.007, 1.6)
  W(0.17, 1.0, 3.4, 0.005, 2.0)
  W(0.19, 1.0, 9.0, 0.0018, 2.8)
  W(0.17, 1.0, 16.0, 0.0016, 3.4)
  W(0.20, 1.0, 27.0, 0.0032, 4.2)
  W(0.18, 1.0, 44.0, 0.0025, 5.0)
  W(0.19, 1.0, 70.0, 0.0017, 5.8)
  W(0.17, 1.0, 110.0, 0.0010, 6.6)
  W(0.19, 1.0, 170.0, 0.0006, 7.4)
  W(1.0, 0.18, 5.0, 0.0015, 0.9)
  return vec3(h, dx, dz);
}

void main() {
  vec2 uv = gl_FragCoord.xy / uRes;
  float aspect = uRes.x / uRes.y;
  float t = uTime * uSpeed;

  // Project the pixel onto the water plane.
  float v = 1.03 - uv.y;
  float d = 1.0 / (v * 1.5);
  vec3 pos = vec3((uv.x - 0.5) * aspect * d * 1.5, 0.0, d);

  vec3 w = waves(pos.xz * 2.4, t);
  vec3 n = normalize(vec3(-w.y * uRipple, 1.0, -w.z * uRipple));
  vec2 jit = vec2(noise(pos.xz * 6.0 + t * 0.7), noise(pos.xz * 6.0 - t * 0.5)) - 0.5;
  n = normalize(n + vec3(jit.x, 0.0, jit.y) * 0.04 * uRipple);

  vec3 cam = vec3(0.0, 1.2, 0.0);
  vec3 V = normalize(cam - pos);
  vec3 L = normalize(vec3(-0.25, 0.30, 1.0));
  float fres = pow(1.0 - max(dot(n, V), 0.0), 3.0);

  vec3 deep = vec3(0.24, 0.50, 0.59);
  vec3 shallow = vec3(0.64, 0.79, 0.83);
  vec3 sky = vec3(0.91, 0.95, 0.96);
  vec3 col = mix(deep, shallow, smoothstep(0.0, 0.75, uv.y));
  col = mix(col, sky, fres * 0.55);

  // Sun sheen, kept calmer on the left where the copy sits.
  vec3 H = normalize(L + V);
  float calm = 1.0 - smoothstep(0.10, 0.55, uv.x);
  float lines = clamp(-w.z * uRipple * 6.0, -1.0, 1.0);
  col += lines * 0.04 * (1.0 - calm * 0.75);
  float sheen = pow(max(dot(n, H), 0.0), 90.0) * (1.0 - calm * 0.7);
  float band = exp(-pow((uv.x - 0.40) * 1.7, 2.0));
  float far = smoothstep(0.12, 0.70, uv.y);
  col += vec3(0.96, 0.98, 0.99) * sheen * uSun * 0.28 * (0.25 + band) * (0.2 + far);
  col += vec3(0.93, 0.96, 0.97) * band * 0.09 * far;

  float corner = smoothstep(0.25, 0.75, abs(uv.x - 0.5)) * (1.0 - smoothstep(0.0, 0.55, uv.y));
  col *= 1.0 - corner * 0.22 * (1.0 - calm * 0.85);

  // Brand-blue haze over the left half, then fade to white toward the horizon.
  float leftHaze = (1.0 - smoothstep(0.08, 0.58, uv.x)) * 0.62;
  col = mix(col, vec3(0.60, 0.80, 0.87), leftHaze);
  float hz = smoothstep(0.25, 1.0, uv.y);
  col = mix(col, vec3(1.0), pow(hz, 1.6) * (0.55 + 0.45 * uHaze) * (1.0 - calm * 0.25));

  gl_FragColor = vec4(col, 1.0);
}
`;

type Props = {
  className?: string;
};

const WaterBackground = ({ className }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const gl = canvas?.getContext('webgl', {
      antialias: false,
      alpha: false,
      premultipliedAlpha: false,
    });

    // Without WebGL the canvas keeps its CSS gradient fallback.
    if (!canvas || !gl) {
      return undefined;
    }

    const compile = (type: number, source: string) => {
      const shader = gl.createShader(type) as WebGLShader;

      gl.shaderSource(shader, source);
      gl.compileShader(shader);

      return shader;
    };

    const program = gl.createProgram() as WebGLProgram;

    gl.attachShader(program, compile(gl.VERTEX_SHADER, VERTEX_SHADER));
    gl.attachShader(program, compile(gl.FRAGMENT_SHADER, FRAGMENT_SHADER));
    gl.linkProgram(program);
    gl.useProgram(program);

    // One quad covering the whole canvas.
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW
    );

    const position = gl.getAttribLocation(program, 'p');

    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const resolution = gl.getUniformLocation(program, 'uRes');
    const time = gl.getUniformLocation(program, 'uTime');

    const draw = (seconds: number) => {
      const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
      const width = Math.floor(canvas.clientWidth * ratio);
      const height = Math.floor(canvas.clientHeight * ratio);

      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }

      gl.uniform2f(resolution, width, height);
      gl.uniform1f(time, seconds);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };

    // Draw straight away so the canvas never flashes black before the first frame.
    draw(0);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const redraw = () => draw(0);

      window.addEventListener('resize', redraw);

      return () => window.removeEventListener('resize', redraw);
    }

    // Stop rendering while the hero is scrolled out of view.
    let isVisible = true;
    const observer = new IntersectionObserver(([entry]) => {
      isVisible = entry.isIntersecting;
    });

    observer.observe(canvas);

    let frame = 0;
    const start = performance.now();
    const loop = (now: number) => {
      frame = requestAnimationFrame(loop);

      if (isVisible) {
        draw((now - start) / 1000);
      }
    };

    frame = requestAnimationFrame(loop);

    // No loseContext() here: StrictMode remounts reuse the same canvas, and a
    // lost context can't be revived. The browser frees it with the canvas.
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return <Canvas ref={canvasRef} className={className} aria-hidden />;
};

const Canvas = styled.canvas`
  display: block;
  width: 100%;
  height: 100%;
  background: linear-gradient(#fff, #8fb8c4 55%, #2f6f80);
`;

export default WaterBackground;
